import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  HostDecision,
  MatchRule,
  Movie,
  ParticipantStateDto,
  SessionCredentials,
  SessionEvent,
  SessionFilters,
  SessionStateDto,
  Vote,
} from '@filmsloth/shared';
import { ApiError, api, type SessionSnapshot } from '../api/client.js';
import { clearCredentials, loadCredentials, saveCredentials } from '../api/storage.js';
import { haptic } from '../lib/haptics.js';
import {
  addPendingVote,
  clearPendingVotes,
  isRetryable,
  loadPendingVotes,
  prunePendingVotes,
  removePendingVote,
  savePendingVotes,
  type PendingVote,
} from '../lib/voteQueue.js';

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'lost';

interface SessionContextValue {
  credentials: SessionCredentials | null;
  state: SessionStateDto | null;
  personal: ParticipantStateDto | null;
  connection: ConnectionState;
  /** True until the stored credentials have been checked against the server. */
  restoring: boolean;
  error: string | null;
  /**
   * True once the host took this device out of the session. The screen says
   * so rather than silently dropping back to the start: somebody who looks
   * up from their phone should learn what happened.
   */
  removed: boolean;
  isHost: boolean;
  /** The movie the group just matched on, still awaiting the host's call. */
  pendingMatch: Movie | null;
  /** Votes cast but not yet accepted by the server — see `voteQueue.ts`. */
  pendingVoteCount: number;
  enter: (credentials: SessionCredentials, state: SessionStateDto) => void;
  vote: (movieId: string, vote: Vote) => Promise<void>;
  /** Signed-in only: drops a card as already seen, without casting a vote. */
  markSeen: (movieId: string) => Promise<void>;
  /** Sends a card to the back of your own deck, unvoted. */
  deferCard: (movieId: string) => Promise<void>;
  /** Takes back your last vote; resolves to the movie that came back. */
  undoVote: () => Promise<Movie | null>;
  start: () => Promise<void>;
  updateFilters: (filters: SessionFilters) => Promise<void>;
  /** Host only, lobby only: how agreeable a film has to be to match. */
  setMatchRule: (rule: MatchRule) => Promise<void>;
  /** Host only: sets the round's clock in minutes, or `null` for none. */
  setTimer: (minutes: number | null) => Promise<void>;
  /** Host only: adds minutes to a clock that is running or has just run out. */
  extendTimer: (minutes: number) => Promise<void>;
  decide: (decision: HostDecision) => Promise<void>;
  /** Host only: opens the shortlist vote once the deck is spent. */
  startRunoff: () => Promise<void>;
  voteRunoff: (movieId: string) => Promise<void>;
  /** Host only: settles a tied shortlist vote. */
  breakRunoffTie: (movieId: string) => Promise<void>;
  finish: () => Promise<void>;
  /**
   * Once the round is over: starts another with the same filters and moves
   * this device into it — or, if somebody already did, follows them in.
   */
  nextRound: () => Promise<void>;
  /** Host only: takes an idle participant out of the round. */
  removeParticipant: (participantId: string) => Promise<void>;
  /** Host only: hands the host role to somebody else. */
  transferHost: (participantId: string) => Promise<void>;
  leave: () => Promise<void>;
  reset: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [credentials, setCredentials] = useState<SessionCredentials | null>(null);
  const [state, setState] = useState<SessionStateDto | null>(null);
  const [personal, setPersonal] = useState<ParticipantStateDto | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('idle');
  const [restoring, setRestoring] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removed, setRemoved] = useState(false);
  /** Set while this device is leaving on its own, which is not a removal. */
  const leavingRef = useRef(false);

  /** Guards against an out-of-order event overwriting newer state. */
  const versionRef = useRef(0);

  /*
   * The outbox. The queue itself lives in a ref rather than in state because
   * the sender walks it and must always see the current one; the count beside
   * it is what the screen renders.
   */
  const pendingRef = useRef<PendingVote[]>([]);
  const [pendingVoteCount, setPendingVoteCount] = useState(0);
  const flushingRef = useRef(false);

  const setPending = useCallback((next: PendingVote[], code: string) => {
    pendingRef.current = next;
    setPendingVoteCount(next.length);
    savePendingVotes(code, next);
  }, []);

  const reset = useCallback(() => {
    clearCredentials();
    clearPendingVotes();
    pendingRef.current = [];
    setPendingVoteCount(0);
    versionRef.current = 0;
    setCredentials(null);
    setState(null);
    setPersonal(null);
    setConnection('idle');
    setError(null);
    setRemoved(false);
  }, []);

  const applyState = useCallback((next: SessionStateDto) => {
    if (next.version < versionRef.current) return;
    versionRef.current = next.version;
    setState(next);
  }, []);

  /**
   * Takes both halves of a command's answer at once.
   *
   * Every command that changes what the caller can swipe answers with the
   * pair, and applying only one half leaves the screen contradicting itself
   * — a queue whose cards the state says are gone, or the other way round.
   */
  const applySnapshot = useCallback(
    (snapshot: SessionSnapshot) => {
      applyState(snapshot.state);
      setPersonal(snapshot.personal);
    },
    [applyState],
  );

  const enter = useCallback(
    (nextCredentials: SessionCredentials, nextState: SessionStateDto) => {
      saveCredentials(nextCredentials);
      pendingRef.current = loadPendingVotes(nextCredentials.code);
      setPendingVoteCount(pendingRef.current.length);
      versionRef.current = 0;
      setCredentials(nextCredentials);
      applyState(nextState);
      setError(null);
      setRemoved(false);
    },
    [applyState],
  );

  // Restore a session after a reload, dropping credentials the server forgot.
  useEffect(() => {
    const stored = loadCredentials();
    if (!stored) {
      setRestoring(false);
      return;
    }
    let cancelled = false;
    api
      .snapshot(stored.token)
      .then(({ state: nextState, personal: nextPersonal }) => {
        if (cancelled) return;
        // A reload in a dead spot is the ordinary way out of one: whatever
        // was cast before it is still owed to the group.
        pendingRef.current = loadPendingVotes(stored.code);
        setPendingVoteCount(pendingRef.current.length);
        setCredentials(stored);
        applySnapshot({ state: nextState, personal: nextPersonal });
      })
      .catch(() => {
        if (!cancelled) clearCredentials();
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applySnapshot]);

  /**
   * Re-fetches the whole session.
   *
   * EventSource reconnects by itself and the server re-publishes on attach, so
   * this is not for the ordinary blip. It is for the case the stream cannot
   * cover: a phone that was asleep or out of signal for minutes, where the
   * events that happened in between are simply gone. Asking once for the
   * current truth is cheaper and more honest than replaying nothing.
   */
  const resync = useCallback(async () => {
    if (!credentials) return;
    try {
      applySnapshot(await api.snapshot(credentials.token));
      setConnection('open');
    } catch {
      // The stream's own retry is still running; leave the status to it.
    }
  }, [credentials, applySnapshot]);

  /**
   * One attempt at one vote.
   *
   * `settled` means the question is closed — the vote is in, or the server
   * refused it for a reason that will not change. `retry` means nobody
   * answered, and the vote belongs in the outbox.
   */
  const sendVote = useCallback(
    async (token: string, movieId: string, choice: Vote): Promise<'settled' | 'retry'> => {
      try {
        applySnapshot(await api.vote(token, movieId, choice));
        setError(null);
        return 'settled';
      } catch (cause) {
        const status = cause instanceof ApiError ? cause.status : null;
        if (isRetryable(status)) return 'retry';
        // A gone session or a rejected token is the end of this session, not
        // of this vote — same as every other action takes it.
        if (status === 404 || status === 401) reset();
        setError(cause instanceof Error ? cause.message : 'Something went wrong.');
        return 'settled';
      }
    },
    [applySnapshot, reset],
  );

  /**
   * Sends what is in the outbox, oldest first, and stops at the first one
   * that fails again. Stopping matters: the votes went out in an order, and
   * pushing past a failure would send the rest of them out of it while the
   * connection is evidently still broken.
   */
  const flushPendingVotes = useCallback(async () => {
    const token = credentials?.token;
    const code = credentials?.code;
    if (!token || !code || flushingRef.current) return;

    const fresh = prunePendingVotes(pendingRef.current, Date.now());
    if (fresh.length !== pendingRef.current.length) setPending(fresh, code);
    if (fresh.length === 0) return;

    flushingRef.current = true;
    try {
      for (const entry of fresh) {
        const outcome = await sendVote(token, entry.movieId, entry.vote);
        if (outcome === 'retry') break;
        setPending(removePendingVote(pendingRef.current, entry.movieId), code);
      }
    } finally {
      flushingRef.current = false;
    }
  }, [credentials, sendVote, setPending]);

  // Once on entering or restoring a session, and then whenever the browser
  // says the network is back or the tab is looked at again.
  useEffect(() => {
    void flushPendingVotes();
  }, [flushPendingVotes]);

  /**
   * A slow heartbeat while anything is waiting. `online` is the signal that
   * usually arrives first, but it fires on an interface coming up rather than
   * on the internet working, so a lone missed vote would otherwise sit there
   * until the next time somebody switched tabs.
   */
  useEffect(() => {
    if (!credentials || pendingVoteCount === 0) return;
    const timer = window.setInterval(() => void flushPendingVotes(), 15000);
    return () => window.clearInterval(timer);
  }, [credentials, pendingVoteCount, flushPendingVotes]);

  /**
   * The browser telling us the network came back, and the tab being looked at
   * again. Both are moments where our picture of the session may be minutes
   * stale, and neither produces an EventSource event on its own.
   */
  useEffect(() => {
    if (!credentials) return;

    const onOnline = () => {
      void resync();
      void flushPendingVotes();
    };
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void resync();
      void flushPendingVotes();
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [credentials, resync, flushPendingVotes]);

  // Live updates. EventSource reconnects on its own; we only mirror the status.
  useEffect(() => {
    if (!credentials) return;
    setConnection('connecting');
    const source = new EventSource(api.eventsUrl(credentials.token));

    const handle = (raw: string) => {
      let event: SessionEvent;
      try {
        event = JSON.parse(raw) as SessionEvent;
      } catch {
        return;
      }
      setConnection('open');
      if (event.type === 'state') applyState(event.state);
      else if (event.type === 'personal') setPersonal(event.state);
    };

    source.onopen = () => setConnection('open');
    source.onerror = () => setConnection('lost');
    source.onmessage = (message) => handle(message.data as string);
    for (const type of ['state', 'personal', 'match', 'finished', 'ping'] as const) {
      source.addEventListener(type, (event) => handle((event as MessageEvent<string>).data));
    }

    return () => source.close();
  }, [credentials, applyState]);

  // The host took this device out. The server has already stopped accepting
  // its token; this is the part where the screen catches up.
  const selfLeft = Boolean(
    state &&
    credentials &&
    state.participants.find((participant) => participant.id === credentials.participantId)?.left,
  );
  useEffect(() => {
    if (!selfLeft) return;
    reset();
    if (!leavingRef.current) setRemoved(true);
  }, [selfLeft, reset]);

  const run = useCallback(
    async <T,>(action: (token: string) => Promise<T>): Promise<T | undefined> => {
      if (!credentials) return undefined;
      try {
        const result = await action(credentials.token);
        setError(null);
        return result;
      } catch (cause) {
        if (cause instanceof ApiError && (cause.status === 404 || cause.status === 401)) {
          reset();
          setError(cause.message);
          return undefined;
        }
        setError(cause instanceof Error ? cause.message : 'Something went wrong.');
        return undefined;
      }
    },
    [credentials, reset],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      credentials,
      state,
      personal,
      connection,
      restoring,
      error,
      removed,
      isHost: Boolean(state && credentials && state.hostId === credentials.participantId),
      pendingMatch: state?.pendingMatch?.movie ?? null,
      pendingVoteCount,
      enter,
      reset,
      vote: async (movieId, vote) => {
        // Every vote passes through here — the buttons, the arrow keys and the
        // swipe alike — so this is the one place the phone has to buzz from.
        // Before the request, not after: the feedback is for the gesture, and
        // a buzz that waits for the network arrives after the next card.
        haptic(vote);
        // Optimistic: drop the card immediately, the stream confirms right after.
        setPersonal((current) =>
          current
            ? {
                ...current,
                queue: current.queue.filter((movie) => movie.id !== movieId),
                votedCount: current.votedCount + 1,
                remaining: Math.max(0, current.remaining - 1),
              }
            : current,
        );
        if (!credentials) return;
        const outcome = await sendVote(credentials.token, movieId, vote);
        if (outcome === 'retry') {
          // Nobody answered. The card is already gone from the screen, so
          // dropping the vote here would quietly cost the group an answer.
          setPending(
            addPendingVote(pendingRef.current, { movieId, vote, at: Date.now() }),
            credentials.code,
          );
        }
      },
      markSeen: async (movieId) => {
        // Optimistic, same as `vote`: the card is gone from the queue on
        // screen right away, without touching `votedCount` — this is not a
        // vote, and shouldn't count as one while the request is in flight.
        setPersonal((current) =>
          current
            ? {
                ...current,
                queue: current.queue.filter((movie) => movie.id !== movieId),
                remaining: Math.max(0, current.remaining - 1),
              }
            : current,
        );
        const result = await run((token) => api.markSeen(token, movieId));
        if (result) applySnapshot(result);
      },
      deferCard: async (movieId) => {
        // Optimistic like `markSeen`, and without touching the counts at all:
        // the card is still yours to answer, just not now.
        setPersonal((current) =>
          current
            ? { ...current, queue: current.queue.filter((movie) => movie.id !== movieId) }
            : current,
        );
        const result = await run((token) => api.deferCard(token, movieId));
        if (result) applySnapshot(result);
      },
      undoVote: async () => {
        // No optimistic update here: which vote comes back depends on server
        // state the client does not track, and guessing wrong would put the
        // wrong card on screen.
        const result = await run((token) => api.undoVote(token));
        if (!result) return null;
        haptic('undo');
        applySnapshot(result);
        return result.personal.queue.find((movie) => movie.id === result.movieId) ?? null;
      },
      start: async () => {
        const result = await run((token) => api.start(token));
        if (result) applySnapshot(result);
      },
      updateFilters: async (filters) => {
        const result = await run((token) => api.updateFilters(token, filters));
        if (result) applyState(result.state);
      },
      setMatchRule: async (rule) => {
        const result = await run((token) => api.setMatchRule(token, rule));
        if (result) applySnapshot(result);
      },
      setTimer: async (minutes) => {
        const result = await run((token) => api.setTimer(token, minutes));
        if (result) applySnapshot(result);
      },
      extendTimer: async (minutes) => {
        const result = await run((token) => api.extendTimer(token, minutes));
        if (result) applySnapshot(result);
      },
      decide: async (decision) => {
        const result = await run((token) => api.decide(token, decision));
        if (result) applyState(result.state);
      },
      startRunoff: async () => {
        const result = await run((token) => api.startRunoff(token));
        if (result) applySnapshot(result);
      },
      voteRunoff: async (movieId) => {
        const result = await run((token) => api.voteRunoff(token, movieId));
        if (result) applySnapshot(result);
      },
      breakRunoffTie: async (movieId) => {
        const result = await run((token) => api.breakRunoffTie(token, movieId));
        if (result) applySnapshot(result);
      },
      finish: async () => {
        const result = await run((token) => api.finish(token));
        if (result) applyState(result.state);
      },
      nextRound: async () => {
        if (!credentials || !state) return;
        const me = state.participants.find(
          (participant) => participant.id === credentials.participantId,
        );
        try {
          const code = state.nextRoundCode;
          const next = code
            ? await api.joinSession(code, me?.name ?? '')
            : await api.startNextRound(credentials.token);
          enter(next.credentials, next.state);
        } catch (cause) {
          // Two people tapping at once: the other one won, and the state that
          // says so is on its way. Following them is the answer either way.
          if (cause instanceof ApiError && cause.code === 'next_round_exists') {
            const fresh = await api.snapshot(credentials.token).catch(() => null);
            const code = fresh?.state.nextRoundCode;
            if (code) {
              try {
                const next = await api.joinSession(code, me?.name ?? '');
                enter(next.credentials, next.state);
                return;
              } catch (joinCause) {
                setError(joinCause instanceof Error ? joinCause.message : 'Something went wrong.');
                return;
              }
            }
          }
          setError(cause instanceof Error ? cause.message : 'Something went wrong.');
        }
      },
      removeParticipant: async (participantId) => {
        const result = await run((token) => api.removeParticipant(token, participantId));
        if (result) applySnapshot(result);
      },
      transferHost: async (participantId) => {
        const result = await run((token) => api.transferHost(token, participantId));
        if (result) applySnapshot(result);
      },
      leave: async () => {
        leavingRef.current = true;
        try {
          await run((token) => api.leave(token));
          reset();
        } finally {
          leavingRef.current = false;
        }
      },
    }),
    [
      credentials,
      state,
      personal,
      connection,
      restoring,
      error,
      removed,
      pendingVoteCount,
      enter,
      reset,
      run,
      applyState,
      applySnapshot,
      sendVote,
      setPending,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}
